import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import {
    ContextInformation,
    Recipient,
} from '../../model/problem-case-creation-info.model';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { HisOption } from '../../../genericsearch/model/his-option';
import { map, Observable, of } from 'rxjs';

export interface ProblemCaseFormValues {
    senderEmailId: number;
    recipientIds: number[];
    subject: string;
    content: string;
}

export interface SenderEmailOption {
    emailId: number;
    label: string;
}

@Component({
    selector: 'his-send-problem-case-form',
    templateUrl: './problem-case-form.component.html',
    styleUrl: './problem-case-form.component.css',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class HisProblemCaseFormComponent {
    @Input() infotext: string | undefined;
    @Input({ required: true }) senderEmails: SenderEmailOption[];
    @Input() initialSenderEmail: SenderEmailOption = undefined;
    @Input() recipientsVisible = true;
    @Input() recipientsEditable = false;
    @Input() recipients: (autocompleteQuery: string) => Observable<Recipient[]>;
    @Input() initialRecipients: Recipient[] = [];
    @Input() subjectVisible = true;
    @Input() initialSubject: string | undefined = undefined;
    @Input() initialContent: string | undefined = undefined;
    @Input() contextInformation: ContextInformation | undefined;
    @Input() showCancelButton = true;
    @Input() disabled = false;

    @Output() submitProblemCaseForm = new EventEmitter<ProblemCaseFormValues>();
    @Output() cancelProblemCaseFrom = new EventEmitter<void>();

    protected filteredSenders: SenderEmailOption[] = [];
    protected pcSenderEmail: SenderEmailOption = undefined;
    protected pcSubject = '';
    protected pcContent = '';
    protected consentDataProcessing = false;
    protected pcSelectedRecipients: HisOption[] = [];

    reset() {
        this.pcSenderEmail = this.initialSenderEmail;
        this.pcSubject = this.initialSubject;
        this.pcContent = this.initialContent;
        this.pcSelectedRecipients = this.initialRecipients.map((e) =>
            this.recipientToHisOption(e),
        );
    }

    protected filterSender(event: AutoCompleteCompleteEvent) {
        if (!this.senderEmails || this.senderEmails.length === 0) {
            return;
        }

        this.filteredSenders = [...this.senderEmails].filter((e) =>
            e.label.toLowerCase().includes(event.query.toLowerCase()),
        );
    }

    protected onSubmitProblemCase(): void {
        if (!this.consentDataProcessing) {
            return;
        }

        const senderEmailId = this.pcSenderEmail?.emailId;
        const subject = this.pcSubject;
        const content = this.pcContent;
        const recipientIds: number[] = this.pcSelectedRecipients.map(
            (e) => +e.id,
        );

        this.submitProblemCaseForm.emit({
            senderEmailId,
            recipientIds,
            subject,
            content,
        });
    }

    protected onCancelProblemCase(): void {
        this.cancelProblemCaseFrom.emit();
    }

    protected onSelectedRecipientsChange(event: HisOption[]): void {
        this.pcSelectedRecipients = event;
    }

    protected getRecipientOptions(): (
        autocompleteQuery: string,
    ) => Observable<HisOption[]> {
        if (!this.recipients) {
            return (autocompleteQuery: string) => of([]);
        }

        return (autocompleteQuery: string) =>
            this.recipients(autocompleteQuery).pipe(
                map((recipients: Recipient[]) =>
                    recipients.map((e) => this.recipientToHisOption(e)),
                ),
            );
    }

    private recipientToHisOption(recipient: Recipient): HisOption {
        return new HisOption(recipient.label, recipient.personId + '');
    }
}
